import type { ReactNode } from 'react';
import { Separator } from '@/components/atoms/separator';

interface WorkspaceHeaderProps {
  contextHeader?: ReactNode;
  toolbarActions?: ReactNode;
  viewToggle?: ReactNode;
  quickActions?: ReactNode;
  newButton?: ReactNode;
  persistentActions?: ReactNode;
}

function HeaderGroup({ children, withSeparator }: { children: ReactNode; withSeparator: boolean }) {
  if (!children) return null;

  return (
    <>
      {withSeparator ? <Separator orientation="vertical" className="h-4" /> : null}
      <div className="flex min-w-0 items-center gap-2">{children}</div>
    </>
  );
}

export function WorkspaceHeader({
  contextHeader,
  toolbarActions,
  viewToggle,
  quickActions,
  newButton,
  persistentActions,
}: WorkspaceHeaderProps) {
  const hasToolbarActions = Boolean(toolbarActions);
  const hasViewToggle = Boolean(viewToggle);
  const hasQuickActions = Boolean(quickActions);
  const hasPersistentActions = Boolean(persistentActions);
  const hasPageHeaderContent = Boolean(
    contextHeader || hasToolbarActions || hasViewToggle || hasQuickActions || newButton,
  );

  // NOT-297: collapse the bar entirely when nothing is slotted, so a dedicated
  // detail page (which provides its own in-page header) doesn't show an empty
  // workspace header strip above it.
  if (!hasPageHeaderContent && !hasPersistentActions) {
    return null;
  }

  return (
    <header
      className={
        hasPageHeaderContent
          ? 'flex min-h-16 shrink-0 items-center gap-3 border-b px-4 py-3'
          : 'hidden min-h-16 shrink-0 items-center gap-3 border-b px-4 py-3 md:flex'
      }
    >
      <div className="min-w-0 flex-1">{contextHeader}</div>

      <div className="ml-auto flex min-w-0 items-center gap-2">
        <HeaderGroup withSeparator={false}>{toolbarActions}</HeaderGroup>
        <HeaderGroup withSeparator={hasToolbarActions}>{viewToggle}</HeaderGroup>
        <HeaderGroup withSeparator={hasToolbarActions || hasViewToggle}>{quickActions}</HeaderGroup>
        <HeaderGroup withSeparator={hasToolbarActions || hasViewToggle || hasQuickActions}>
          {newButton}
        </HeaderGroup>
        <HeaderGroup
          withSeparator={
            hasToolbarActions || hasViewToggle || hasQuickActions || Boolean(newButton)
          }
        >
          {persistentActions}
        </HeaderGroup>
      </div>
    </header>
  );
}
